export const ROLES_PERMISSIONS_GUIDE = `# Roles & Permissions Quick Reference

## Overview

VoiceThera uses a role-based access control (RBAC) system to determine what users can see and do. This guide provides a quick reference for permissions across all user roles.

---

## User Roles

### Individual User

Personal account for self-directed wellness.

**Tiers:**
- **Free**: 60 signup credits, no subscription
- **Basic** ($29/month): 100 monthly credits
- **Pro** ($79/month): 300 monthly credits

**Primary use case**: Personal meditation and wellness sessions.

### Practitioner

Professional account for serving clients.

**Tiers:**
- **Free**: 120 signup credits, no subscription
- **Basic** ($49/month): 200 monthly credits
- **Pro** ($129/month): 600 monthly credits

**Primary use case**: Creating personalized sessions for clients, managing practitioner-client relationships.

### Organization Employee

Member of an organization (company, nonprofit, school, etc.).

**No subscription required** (organization provides credits).

**Primary use case**: Creating wellness sessions using organizational credits and context, switching between personal and employee modes.

### Organization Admin

Administrator of an organization account.

**Roles:**
- **Owner**: Creator of organization, full control, cannot be removed
- **Admin**: Added by owner, full management permissions
- **Department Head**: Limited visibility (department analytics only)

**Primary use case**: Managing organization profile, employees, departments, credits, and practitioner access.

### System Admin

VoiceThera internal staff (support, engineering, leadership).

**Not a user-accessible role**.

**Primary use case**: Platform administration, user support, analytics.

---

## Permission Matrix

### Core Capabilities

| Capability | Individual | Practitioner | Employee | Org Admin | Sys Admin |
|------------|-----------|-------------|----------|-----------|-----------|
| **Sessions & Content** |
| Create personal sessions | ✅ | ✅ | ✅ | ✅ | ✅ |
| Create client sessions | ❌ | ✅ | ❌ | ❌ | ✅ |
| Create org sessions (employee mode) | ❌ | ❌ | ✅ | ✅ | ✅ |
| Use literal script mode | ❌ | ✅ | ❌ | ❌ | ✅ |
| Access all meditation categories | ✅ | ✅ | ✅ | ✅ | ✅ |
| Generate session audio | ✅ | ✅ | ✅ | ✅ | ✅ |
| Download session audio | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Biography & Personalization** |
| Edit personal biography | ✅ | ✅ | ✅ | ✅ | ✅ |
| Edit client biographies | ❌ | ✅ | ❌ | ❌ | ✅ |
| Edit organization biography | ❌ | ❌ | ❌ | ✅ | ✅ |
| Set session directives | ❌ | ✅ | ❌ | ❌ | ✅ |
| Set content requirements/exclusions | ❌ | ✅ | ❌ | ❌ | ✅ |
| **Client Management** |
| Manage clients (add, edit, deactivate) | ❌ | ✅ | ❌ | ❌ | ✅ |
| Link clients to organizations | ❌ | ✅* | ❌ | ❌ | ✅ |
| View client session history | ❌ | ✅ | ❌ | ❌ | ✅ |
| Delete client records | ❌ | ✅ | ❌ | ❌ | ✅ |
| **Group Sessions** |
| Create group sessions | ✅ | ✅ | ✅ | ✅ | ✅ |
| Manage groups | ✅ | ✅ | ✅ | ✅ | ✅ |
| Add/remove group participants | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Group limits** | See tier | See tier | See tier | See tier | Unlimited |

*With explicit organization access grant

### Organization Capabilities

| Capability | Individual | Practitioner | Employee | Org Admin | Sys Admin |
|------------|-----------|-------------|----------|-----------|-----------|
| **Organization Setup** |
| Create organization | ❌ | ❌ | ❌ | Owner only | ✅ |
| Edit organization profile | ❌ | ❌ | ❌ | ✅ | ✅ |
| Delete organization | ❌ | ❌ | ❌ | Owner only | ✅ |
| **Employee Management** |
| View organization employees | ❌ | ✅* | ✅** | ✅ | ✅ |
| Invite employees | ❌ | ❌ | ❌ | ✅ | ✅ |
| Deactivate employees | ❌ | ❌ | ❌ | ✅ | ✅ |
| Edit employee roles | ❌ | ❌ | ❌ | ✅ | ✅ |
| View employee session history | ❌ | ❌ | ❌ | ✅ (summary only) | ✅ |
| View employee session content | ❌ | ❌ | ❌ | ❌ | ✅ (support only) |
| **Department Management** |
| Create departments | ❌ | ❌ | ❌ | ✅ | ✅ |
| Edit departments | ❌ | ❌ | ❌ | ✅ | ✅ |
| Assign department heads | ❌ | ❌ | ❌ | ✅ | ✅ |
| Delete departments | ❌ | ❌ | ❌ | ✅ | ✅ |
| Assign employees to departments | ❌ | ❌ | ❌ | ✅ | ✅ |
| **Credits & Billing** |
| View org credit balance | ❌ | ❌ | ✅*** | ✅ | ✅ |
| Purchase organization credits | ❌ | ❌ | ❌ | ✅ | ✅ |
| Set employee allowances | ❌ | ❌ | ❌ | ✅ | ✅ |
| View usage analytics | ❌ | ❌ | ❌ | ✅ | ✅ |
| View transaction logs | ❌ | ❌ | ❌ | ✅ | ✅ |
| Export reports | ❌ | ❌ | ❌ | ✅ | ✅ |
| **Practitioner Access** |
| Grant practitioner access | ❌ | ❌ | ❌ | ✅ | ✅ |
| Revoke practitioner access | ❌ | ❌ | ❌ | ✅ | ✅ |
| Set access expiration dates | ❌ | ❌ | ❌ | ✅ | ✅ |
| View access audit logs | ❌ | ❌ | ❌ | ✅ | ✅ |

*With explicit organization access grant
**Coworkers only (same organization)
***Own allowance only, not full org balance

### Credits & Payments

| Capability | Individual | Practitioner | Employee | Org Admin | Sys Admin |
|------------|-----------|-------------|----------|-----------|-----------|
| **Personal Credits** |
| Purchase credit packs | ✅ | ✅ | ✅ | ✅ | ✅ |
| Subscribe to monthly plan | ✅ | ✅ | ✅ | ✅ | ✅ |
| View personal credit balance | ✅ | ✅ | ✅ | ✅ | ✅ |
| View personal transaction history | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Organization Credits** |
| View own allowance | ❌ | ❌ | ✅ | ✅ | ✅ |
| View allowance reset date | ❌ | ❌ | ✅ | ✅ | ✅ |
| **Context Switching** |
| Switch between personal/employee mode | ❌ | ❌ | ✅ | ✅ | ✅ |
| View which credits will be used | ✅ | ✅ | ✅ | ✅ | ✅ |

### Administrative

| Capability | Individual | Practitioner | Employee | Org Admin | Sys Admin |
|------------|-----------|-------------|----------|-----------|-----------|
| **Account Management** |
| Edit own profile | ✅ | ✅ | ✅ | ✅ | ✅ |
| Delete own account | ✅ | ✅ | ✅ | ✅ | ✅ |
| Change email/password | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Notifications** |
| Manage notification preferences | ✅ | ✅ | ✅ | ✅ | ✅ |
| Receive system alerts | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Support** |
| Contact support | ✅ | ✅ | ✅ | ✅ | ✅ |
| View documentation | ✅ | ✅ | ✅ | ✅ | ✅ |
| **Platform Administration** |
| Access admin dashboard | ❌ | ❌ | ❌ | ❌ | ✅ |
| View all users | ❌ | ❌ | ❌ | ❌ | ✅ |
| Manage subscription plans | ❌ | ❌ | ❌ | ❌ | ✅ |
| View platform analytics | ❌ | ❌ | ❌ | ❌ | ✅ |

---

## Row-Level Security (RLS) Policies

VoiceThera uses PostgreSQL Row-Level Security to enforce permissions at the database level.

### Key RLS Concepts

**Policy types:**
- **SELECT**: Who can view records
- **INSERT**: Who can create records
- **UPDATE**: Who can modify records
- **DELETE**: Who can delete records

**Common patterns:**
- \`auth.uid() = user_id\`: User can only access their own records
- \`organization_id IN (SELECT ...)\`: User can access organization records if they belong to that org
- \`status = 'active' AND ...\`: Additional filters beyond user identity

### Profile Visibility

**profiles table SELECT policies:**

1. **Own profile**: Users can always view their own profile
   - \`auth.uid() = id\`

2. **Coworker profiles**: Employees can view profiles of active coworkers in the same organization
   - User has active employee record in organization
   - Target user has active employee record in same organization

3. **Admin access**: Organization admins can view profiles of any active employees in their organization
   - User is admin of organization (via organization_admins table)
   - Target user is active employee in that organization

4. **Practitioner access**: Practitioners with access grants can view employee profiles in granted organizations
   - Practitioner has active, non-expired grant in practitioner_organization_access table
   - Employee belongs to that organization

**Without these policies**: Names would show as "Employee" placeholder, dropdowns would be empty.

### Organization Visibility

**organizations table SELECT policies:**

1. **Owner access**: Organization owner can view their organization
   - \`auth.uid() = owner_user_id\`

2. **Admin access**: Organization admins can view their organizations
   - User is admin via organization_admins table

3. **Employee access**: Employees can view organizations they belong to
   - User has active employee record in organization_employees table

4. **Practitioner access**: Practitioners can view organizations that granted them access
   - User has active, non-expired grant in practitioner_organization_access table

### Organization Employee Visibility

**organization_employees table SELECT policies:**

1. **Own employee record**: Users can view their own employee records
   - \`auth.uid() = user_id\`

2. **Coworker access**: Employees can view other employees in the same organization
   - User has active employee record in organization
   - Target employee is in same organization

3. **Admin access**: Organization admins can view all employees in their organizations
   - User is admin of organization

4. **Practitioner access**: Practitioners with access grants can view employees in granted organizations
   - Practitioner has active, non-expired grant
   - Employees belong to granted organization

**Invitation access exception**: Unauthenticated users with valid invitation tokens can view organization details before signup (enables viewing org context in accept invitation flow).

### Session Visibility

**sessions table SELECT policies:**

1. **Creator access**: Users can view sessions they created
   - \`auth.uid() = user_id\`

2. **Assigned client access**: Practitioners can view sessions assigned to their clients
   - Session assigned to client owned by practitioner

3. **Shared link access**: Anyone with valid shared link token can view session
   - Token matches shared_session_links table
   - Token not expired
   - Link is active

**Organization admins CANNOT view session content** (privacy protection). Admins see usage analytics only.

---

## Access Grant Workflow

### Practitioner-Organization Access

**Step-by-step:**

1. **Organization admin grants access**
   - Navigate to Settings → Practitioner Access
   - Enter practitioner email
   - Set optional expiration date
   - Confirm grant

2. **System creates access record**
   - Insert into practitioner_organization_access table
   - Status: 'active'
   - Granted timestamp recorded

3. **RLS policies activate**
   - Organizations table: Practitioner can now SELECT organization
   - Organization_employees table: Practitioner can SELECT employees
   - Profiles table: Practitioner can SELECT employee profile data

4. **Practitioner sees organization**
   - Organization appears in client form dropdown
   - Can select organization → see employee list
   - Can link clients to employees

5. **Session creation with org context**
   - Practitioner creates session for org-linked client
   - Organization credits consumed (not practitioner's personal credits)
   - Session tagged with organization_id

6. **Revocation (if needed)**
   - Admin revokes access → status changed to 'revoked'
   - RLS policies immediately prevent access
   - Practitioner loses visibility of organization

**Access expiration:**
- If expiration date set, status auto-changes to 'expired' at midnight on that date
- Same effect as revocation (immediate loss of access)

---

## Context Switching (Employee vs Personal Mode)

### How Context Switching Works

Organization employees can operate in two distinct modes:

**Personal Mode (Default):**
- Context: Individual user, no organization affiliation
- Credits: Personal credit pool (subscription or purchased)
- Biography: Personal biography only
- Sessions: No organization context, completely private
- Analytics: Not visible to organization

**Employee Mode (Organization Selected):**
- Context: Organization member
- Credits: Organization credit pool (monthly allowance)
- Biography: Personal + organization biography blended
- Sessions: Organization context integrated, values referenced
- Analytics: Visible to organization admins (usage only, not content)

### Context Switcher UI

**Location**: Dashboard header, session creation form

**Options:**
- 👤 **Personal** - "Using my credits"
- 🏢 **[Org Name]** - "Using work credits"

**Behavior:**
- Switching context updates credit source indicator
- Session generation adjusts prompt based on context
- Clear visual feedback on which mode is active

### Permission Implications

**In personal mode:**
- ❌ Cannot see organization employees (unless coworker lookup needed for separate reason)
- ❌ Cannot access organization credit balance
- ✅ Full privacy from organization

**In employee mode:**
- ✅ Can see coworker names (for collaboration features)
- ✅ Can view own allowance and usage
- ❌ Cannot see other employees' usage details
- ❌ Cannot access organization admin functions

---

## Frequently Asked Questions

### Roles

**Q: Can one user have multiple roles?**
A: Yes. A user can be:
- Individual (Free/Basic/Pro)
- Practitioner (Free/Basic/Pro) ← Same user, additional capabilities
- Employee of multiple organizations
- Admin of multiple organizations

**Q: How do I upgrade from Individual to Practitioner?**
A: Navigate to Settings → Account → Upgrade to Practitioner. You keep all individual features and gain practitioner capabilities.

**Q: Can organization admins also be employees?**
A: Yes, admins can (and often are) also employees. Admin permissions are separate from employee membership.

### Permissions

**Q: Why can't I see an organization in the dropdown?**
A: Practitioners only see organizations that explicitly granted them access. Employees only see organizations they belong to. Contact organization admin for access.

**Q: Can I see other practitioners' clients?**
A: No, client records are isolated per practitioner. Even if two practitioners have access to the same organization, they cannot see each other's client lists.

**Q: Why can't I see employee names in the dropdown?**
A: RLS policies restrict profile visibility. You need either:
- Active employee membership in that organization (coworker access), OR
- Active admin role in that organization, OR
- Active practitioner access grant for that organization

**Q: Can organization admins see my session content?**
A: No. Admins see usage analytics (date, duration, category, credits) but NOT session content, biography, or feedback. Session content is always private.

### Access Grants

**Q: How long does an access grant take to activate?**
A: Immediate. Once admin confirms grant, practitioner can see organization within seconds (may need to refresh page).

**Q: Can I request access to an organization?**
A: No, access is organization-initiated. Contact the organization admin directly to request access.

**Q: What happens to sessions if access is revoked?**
A: Existing sessions remain accessible to clients and practitioners. No new sessions can be created for org-linked clients after revocation.

### Credits

**Q: Who pays when I create a session in employee mode?**
A: The organization pays from their credit pool (your monthly allowance). Your personal credits are not affected.

**Q: What if I'm a practitioner AND an employee?**
A: You can switch contexts:
- Personal mode: Your practitioner credits
- Employee mode (Org A): Org A's credits
- Employee mode (Org B): Org B's credits
The context switcher makes it clear which credits will be used.

---

## Support

For permission or access issues:
- Email: support@voicethera.com
- Chat: In-app support
- Related guides: Organization Admin Guide, Client Management Guide

**Last Updated**: January 2025
**Version**: 1.0
`;
